import { imageStorageAPI, getImageSpaceUploadFolder } from './api';
import { getImageFromIndexedDB } from './imageStorage';
import {
  toAbsoluteViewPath,
  isManagedStorageUrl,
  isOssDirectDisplayUrl,
  isSignedDisplayUrl,
  isSignedViewEnabled,
  isUploadsStoragePath,
  resolveDisplayImageUrl,
  resolveImmediateSignedSrc,
  resolveSignedDisplayUrl,
  pickImageSignOptions,
  sanitizeManagedDisplayResult,
  warmDisplayCacheFromRecords,
  toManagedCanonicalUploadsUrl,
  ensureDisplayImageUrls,
} from './imageDisplayAccess';
import { lookupUploadViewUrl } from './uploadBatchViewUrls';

// 前端图片展示用公网 base（优先 PUBLIC_BASE_URL，其次 API_URL 去掉 /api，否则当前页 origin）
function resolvePublicBaseUrlEnv() {
  let pub = (process.env.REACT_APP_PUBLIC_BASE_URL || '').trim().replace(/\/$/, '');
  if (pub && typeof window !== 'undefined' && window.location.protocol === 'https:' && pub.startsWith('http://')) {
    pub = pub.replace(/^http:\/\//i, 'https://');
  }
  return pub;
}

function resolveApiBaseForAsset() {
  const raw = (process.env.REACT_APP_API_URL || '').trim();
  if (raw.startsWith('/')) return '';
  let base = raw.replace(/\/api\/?$/, '').replace(/\/$/, '');
  if (base && typeof window !== 'undefined' && window.location.protocol === 'https:' && base.startsWith('http://')) {
    base = base.replace(/^http:\/\//i, 'https://');
  }
  return base;
}

const resolveAssetBaseUrl = () => {
  const pub = resolvePublicBaseUrlEnv();
  if (pub) return pub;
  const fromApi = resolveApiBaseForAsset();
  if (fromApi) return fromApi;
  return typeof window !== 'undefined' ? window.location.origin : '';
};

export {
  ensureDisplayImageUrls,
  pickImageSignOptions,
  resolveDisplayImageUrl,
  resolveImmediateSignedSrc,
  resolveSignedDisplayForUrl,
  resolveSignedDisplayUrl,
  sanitizeManagedDisplayResult,
  isManagedStorageUrl,
  isOssManagedUrl,
  isSignedViewEnabled,
} from './imageDisplayAccess';
export const isLocalOnlyUrl = (url) => !!(url && String(url).startsWith('indexeddb://'));

function isObjectStorageHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  return (h.includes('.oss-') && h.includes('aliyuncs.com'))
    || h.includes('.s3.')
    || h.includes('.amazonaws.com')
    || h.includes('cloudfront.net')
    || h.includes('.myqcloud.com');
}

/**
 * 展示/存储用图片名称：去掉常见图片后缀（如 .jpg / .png），避免把格式当成名称一部分。
 */
export function stripImageFileExtension(name) {
  return String(name || '')
    .trim()
    .replace(/\.(png|jpe?g|webp|gif|bmp|svg|heic|avif|tiff?)$/i, '');
}

/**
 * 规范化为可持久化的图片地址：本站 /uploads/ 相对路径，或 OSS/CDN 完整 https URL。
 * data URL、blob、indexeddb 等返回空串。
 */
export function normalizePersistedImageUrl(maybe) {
  if (!maybe || typeof maybe !== 'string') return '';
  let s = maybe.trim().replace(/\\/g, '/');
  if (!s || s.startsWith('data:')) return '';
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const u = new URL(s);
      if (u.pathname.startsWith('/uploads/')) return u.pathname;
      // 受管 OSS/view（含 x-oss-process）一律还原为 /uploads/{key}，勿把预签名当 canonical
      const canonical = toManagedCanonicalUploadsUrl(s);
      if (canonical) return canonical;
      if (isObjectStorageHost(u.hostname)) return s;
    } catch {
      return '';
    }
    return '';
  }
  if (s.startsWith('/uploads/')) return s;
  if (s.startsWith('uploads/')) return `/${s}`;
  const idx = s.indexOf('/uploads/');
  if (idx >= 0) return s.slice(idx);
  return '';
}

/** 是否为已上传、可写入图片空间/跨设备同步的服务端图片地址 */
export function isPersistedImageUrl(url) {
  return Boolean(normalizePersistedImageUrl(url));
}

/**
 * 从受管签名读图 URL（/api/storage/images/view?k=...）还原为 /uploads/{key}，
 * 供万能改图/试衣 API 提交（后端 Img2ImgImageResolver 可读）。
 */
export function uploadsPathFromSignedViewUrl(maybe) {
  if (!maybe || typeof maybe !== 'string') return '';
  const trimmed = maybe.trim();
  if (!trimmed) return '';
  try {
    const parsed = trimmed.startsWith('http://') || trimmed.startsWith('https://')
      ? new URL(trimmed)
      : new URL(trimmed, 'http://local.invalid');
    if (!parsed.pathname.includes('/api/storage/images/view')) return '';
    const key = parsed.searchParams.get('k');
    if (!key) return '';
    const decoded = decodeURIComponent(key).replace(/\\/g, '/').replace(/^\/+/, '');
    if (!decoded || decoded.includes('..')) return '';
    return `/uploads/${decoded}`;
  } catch {
    return '';
  }
}

/** 将 data URL 转为 Blob（不可对 data URL 使用 fetch，浏览器会报 ERR_INVALID_URL） */
export function dataUrlToBlob(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') {
    throw new Error('无效的 data URL');
  }
  if (!dataUrl.startsWith('data:')) {
    throw new Error('不是有效的 data URL 格式');
  }
  const arr = dataUrl.split(',');
  if (arr.length < 2) {
    throw new Error('data URL 格式不正确');
  }
  const mimeMatch = arr[0].match(/:(.*?);/);
  if (!mimeMatch?.[1]) {
    throw new Error('无法从 data URL 中提取 MIME 类型');
  }
  const bstr = atob(arr[1]);
  const u8arr = new Uint8Array(bstr.length);
  for (let i = 0; i < bstr.length; i += 1) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return new Blob([u8arr], { type: mimeMatch[1] });
}

/**
 * 将图片 URL（data / blob / http）转为 Blob。
 * OSS 直链用 cors + 不带 cookie，避免触发预检失败。
 * @param {string} url - 已解析的可 fetch 地址或 data URL
 */
export async function urlToImageBlob(url) {
  const u = String(url || '').trim();
  if (!u) throw new Error('EMPTY_IMAGE_URL');
  if (u.startsWith('data:')) return dataUrlToBlob(u);
  const response = await fetch(u, {
    credentials: 'omit',
    mode: 'cors',
    cache: 'no-cache',
  });
  if (!response.ok) throw new Error(`FETCH_${response.status}`);
  return response.blob();
}

/** 相对路径先签名读图，再按候选链拉取为 Blob（HMAC view 或 OSS 预签名直链）。
 * 不再在 Blob 失败后走 DataUrl 兜底：对 CORS/403 通常无效，只会多一次延迟。
 */
export async function resolveImageUrlToBlob(url, options = {}) {
  const u = String(url || '').trim();
  if (!u) throw new Error('EMPTY_IMAGE_URL');
  if (u.startsWith('data:') || u.startsWith('blob:')) return urlToImageBlob(u);

  const candidates = await buildFetchCandidates(u, options);
  if (!candidates.length) throw new Error('EMPTY_SIGNED_URL');

  let lastError = null;
  for (const candidate of candidates) {
    const target = toAbsoluteViewPath(candidate);
    if (!target) continue;
    try {
      return await urlToImageBlob(target);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('EMPTY_SIGNED_URL');
}

function isSignedDisplayFetchUrl(url) {
  return isSignedDisplayUrl(url);
}

/** signed-only 下不应 fetch 的裸 /uploads/ 直链（会 403） */
function shouldOmitBareUploadsFetchCandidate(url) {
  return isSignedViewEnabled()
    && isUploadsStoragePath(url)
    && !isSignedDisplayFetchUrl(url);
}

/** OSS 预签名/CDN 直链不要带 Authorization，否则易触发 CORS 预检失败 */
function shouldAttachAuthHeaderForImageFetch(url) {
  const s = String(url || '').trim();
  if (!s) return false;
  if (isOssDirectDisplayUrl(s)) return false;
  if (isSignedDisplayFetchUrl(s) && (s.startsWith('http://') || s.startsWith('https://'))) {
    try {
      if (typeof window !== 'undefined' && window.location?.origin) {
        const host = new URL(s, window.location.origin).origin;
        if (host !== window.location.origin) return false;
      }
    } catch {
      return false;
    }
  }
  return true;
}

/**
 * 构建 fetch 尝试顺序：signed-only 时优先签名展示地址（HMAC view 或 OSS 直链），不含裸 /uploads/。
 */
export async function buildFetchCandidates(rawUrl, itemOrOptions = {}) {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return [];

  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return [trimmed];
  }

  const options = itemOrOptions && typeof itemOrOptions === 'object'
    && itemOrOptions.displayUrl == null
    && itemOrOptions.viewUrl == null
    && (itemOrOptions.id || itemOrOptions.url != null || itemOrOptions.dataUrl != null)
      ? pickImageSignOptions(itemOrOptions)
      : (itemOrOptions || {});

  const candidates = [];
  const seen = new Set();
  const add = (u) => {
    const s = String(u || '').trim();
    if (!s || seen.has(s)) return;
    if (shouldOmitBareUploadsFetchCandidate(s)) return;
    seen.add(s);
    candidates.push(s);
  };

  const signed = await resolveFetchableImageUrl(trimmed, options);
  if (signed) {
    add(signed);
    add(toAbsoluteViewPath(signed));
  }
  const sync = resolveDisplayImageUrl(trimmed, options);
  if (sync) add(toAbsoluteViewPath(sync));

  if (!isSignedViewEnabled() || !isUploadsStoragePath(trimmed)) {
    add(trimmed);
    add(rebaseUploadsUrl(trimmed));
    const uploadsPath = normalizePersistedImageUrl(trimmed);
    if (uploadsPath) {
      const apiRoot = (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
      if (apiRoot) add(`${apiRoot}${uploadsPath}`);
      const assetBase = resolveAssetBaseUrl();
      if (assetBase) add(`${assetBase}${uploadsPath}`);
    }
  }

  return candidates;
}

async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * 按候选链 fetch 图片并转为 data URL；signed-only 下跳过裸 /uploads/；OSS 直链不带 Authorization。
 * @param {object} [options] - { itemOrOptions, getAuthToken: () => string|null }
 */
export async function fetchImageUrlAsDataUrl(rawUrl, options = {}) {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('data:image/')) return trimmed;

  const { itemOrOptions, getAuthToken } = options;
  const candidates = await buildFetchCandidates(trimmed, itemOrOptions);
  const token = typeof getAuthToken === 'function' ? getAuthToken() : null;

  for (const u of candidates) {
    if (!u || u.startsWith('data:')) continue;
    try {
      const headers = {};
      if (token && shouldAttachAuthHeaderForImageFetch(u)) {
        headers.Authorization = `Bearer ${token}`;
      }
      const res = await fetch(u, {
        credentials: 'omit',
        mode: 'cors',
        cache: 'no-cache',
        headers,
      });
      if (!res.ok) continue;
      const blob = await res.blob();
      if (!blob.type || !blob.type.startsWith('image/')) continue;
      const dataUrl = await blobToDataUrl(blob);
      if (dataUrl.startsWith('data:image/')) return dataUrl;
    } catch {
      /* try next */
    }
  }
  return '';
}

/**
 * 任意图片地址 → data URL（设计器落库 / 合成）。blob/data 直转；受管存储先换签再拉。
 */
export async function resolveImageUrlToDataUrl(url, options = {}) {
  const u = String(url || '').trim();
  if (!u) return '';
  if (u.startsWith('data:image/')) return u;
  if (u.startsWith('blob:')) {
    try {
      const blob = await urlToImageBlob(u);
      return blobToDataUrl(blob);
    } catch {
      return '';
    }
  }
  return fetchImageUrlAsDataUrl(u, { itemOrOptions: options });
}

/**
 * 从 signed-image 等接口响应中取出可用的持久化图片地址（/uploads/ 或 OSS https URL）。
 * 不可使用顶层 result.path：Spring 兼容层曾用它表示路由键（如 "signed-image"），不是文件路径。
 */
export function pickUrlFromImageStorageResponse(result) {
  if (!result || typeof result !== 'object') return '';
  const candidates = [result.url, result.viewUrl, result.data?.url, result.data?.viewUrl, result.data?.path, result.data?.fileUrl];
  for (const c of candidates) {
    const n = normalizePersistedImageUrl(c);
    if (n) return n;
  }
  return '';
}

/** 上传完成后解析可展示的预览地址（优先后端 viewUrl/displayUrl，可为 HMAC 或 OSS 直链） */
export async function resolveUploadPreviewUrl(storedUrl, uploadResult) {
  const trimmed = String(storedUrl || '').trim();
  if (!trimmed) return '';
  const signOptions = {
    viewUrl: uploadResult?.viewUrl,
    displayUrl: uploadResult?.displayUrl,
    storedUrlDisplay: uploadResult?.storedUrlDisplay,
    storedViewUrl: uploadResult?.storedViewUrl,
    ...(uploadResult?.variant != null ? { variant: uploadResult.variant } : {}),
  };
  if (signOptions.viewUrl || signOptions.displayUrl || signOptions.storedViewUrl || signOptions.storedUrlDisplay) {
    warmDisplayCacheFromRecords([{ url: trimmed, ...signOptions, id: uploadResult?.id }], (item) => item.url);
  }
  return resolveSignedDisplayUrl(trimmed, signOptions);
}

/** 素材库/图片空间条目：优先 displayUrl，否则批量签名 */
export async function resolveItemPreviewUrl(storedUrl, item) {
  const trimmed = String(storedUrl || '').trim();
  if (!trimmed) return '';
  return resolveFetchableImageUrl(trimmed, item);
}

/**
 * fetch / canvas 转码 / 工作流拉图用：无 viewUrl 缓存时自动 sign-batch。
 * 同步 API（getFullImageUrl / resolveDisplayImageUrl）无缓存返回空串时，应 await 本函数。
 */
export async function resolveFetchableImageUrl(rawUrl, itemOrOptions) {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return '';
  const options = itemOrOptions && typeof itemOrOptions === 'object'
    && itemOrOptions.displayUrl == null
    && itemOrOptions.viewUrl == null
    && (itemOrOptions.id || itemOrOptions.url != null || itemOrOptions.dataUrl != null)
      ? pickImageSignOptions(itemOrOptions)
      : (itemOrOptions || {});
  const signed = await resolveSignedDisplayUrl(trimmed, options);
  return sanitizeManagedDisplayResult(signed, trimmed);
}

/** Studio 页本地上传：dataUrl 先落盘，再返回持久化 URL 与签名预览 URL */
export async function resolveStudioFileUploadPreview(item, uploadOptions = {}) {
  const results = await resolveStudioFilesUploadPreview([item], uploadOptions);
  return results[0] || { storedUrl: '', previewUrl: '', uploadResult: null, imageSpaceId: null };
}

/**
 * 批量工作室上传：走 saveBatch（一次 capabilities + 分块上传），避免每张图单独探测与拉全量去重。
 */
export async function resolveStudioFilesUploadPreview(items, uploadOptions = {}) {
  const {
    folder = 'image-space',
    source,
    registerImageSpace = true,
  } = uploadOptions;
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (list.length === 0) return [];

  const prepared = [];
  for (let i = 0; i < list.length; i += 1) {
    const item = list[i];
    let storedUrl = item?.url || item?.imageUrl || item?.serverUrl || '';
    const raw = String(storedUrl || '').trim();
    if ((!raw || raw.startsWith('data:')) && item?.dataUrl) {
      prepared.push({ index: i, item, needsUpload: true });
    } else if (raw && !raw.startsWith('data:')) {
      prepared.push({ index: i, item, needsUpload: false, storedUrl: raw });
    } else {
      prepared.push({ index: i, item, needsUpload: false, storedUrl: '' });
    }
  }

  const needUpload = prepared.filter((row) => row.needsUpload);
  /** @type {Map<number, object>} */
  const uploadByIndex = new Map();

  if (needUpload.length === 1) {
    const row = needUpload[0];
    const item = row.item;
    const uploadResult = await imageStorageAPI.save(item.dataUrl, {
      folder,
      source: source ?? item?.source,
      size: uploadOptions.size ?? item?.size ?? item?.originalSize ?? null,
      width: uploadOptions.width ?? item?.width ?? null,
      height: uploadOptions.height ?? item?.height ?? null,
      id: uploadOptions.id ?? item?.id ?? null,
      name: uploadOptions.name ?? item?.name ?? null,
      registerImageSpace,
    });
    uploadByIndex.set(row.index, uploadResult);
  } else if (needUpload.length > 1) {
    const { buildUploadFileFromBlob } = await import('./ipeImageUploadFormats');
    const files = [];
    const fileIndexes = [];
    for (const row of needUpload) {
      const dataUrl = String(row.item?.dataUrl || '').trim();
      if (!dataUrl.startsWith('data:')) continue;
      try {
        const blob = dataUrlToBlob(dataUrl);
        const file = await buildUploadFileFromBlob(blob, {
          filename: row.item?.name || `upload-${row.index + 1}.jpg`,
          dataUrlHint: dataUrl,
        });
        files.push(file);
        fileIndexes.push(row.index);
      } catch (error) {
        console.warn('[studio-upload] dataUrl 转 File 失败', error);
      }
    }
    if (files.length > 0) {
      const batchResult = await imageStorageAPI.saveBatch(files, {
        folder,
        source,
        registerImageSpace,
      });
      mapBatchUploadResults(uploadByIndex, fileIndexes, batchResult);
    }
  }

  return finalizeStudioUploadResults(prepared, uploadByIndex);
}

/** 本地 File/Blob 即时预览；移除原图时请 revokeStudioPreviewUrl */
export function createStudioLocalPreviewUrl(file) {
  if (!(file instanceof Blob)) return '';
  try {
    return URL.createObjectURL(file);
  } catch {
    return '';
  }
}

export function revokeStudioPreviewUrl(url) {
  const s = String(url || '').trim();
  if (!s.startsWith('blob:')) return;
  try {
    URL.revokeObjectURL(s);
  } catch {
    /* ignore */
  }
}

/**
 * 工作室原图直传：File[] → saveBatch，跳过 processMultipleImageUploads 的双重 base64。
 * 可消除 Network 中成百上千条 data:image「请求」，并避免二次压缩。
 * 预览优先 blob，避免上传后立刻批量 eager 签图/读图导致 429 裂图。
 */
export async function uploadStudioSourceFiles(files, uploadOptions = {}) {
  const {
    folder = 'image-space',
    source,
    registerImageSpace = true,
  } = uploadOptions;
  const list = (Array.isArray(files) ? files : []).filter((f) => f instanceof Blob);
  if (list.length === 0) return [];

  const withLocalPreview = (file, remotePreview) => {
    const local = createStudioLocalPreviewUrl(file);
    return local || remotePreview || '';
  };

  if (list.length === 1) {
    const file = list[0];
    const uploadResult = await imageStorageAPI.save(file, {
      folder,
      source,
      size: file.size || null,
      name: file.name || null,
      registerImageSpace,
    });
    const storedUrl = String(uploadResult?.url || uploadResult?.imageUrl || '').trim();
    if (!storedUrl || storedUrl.startsWith('data:')) {
      return [{
        storedUrl: '',
        previewUrl: withLocalPreview(file, ''),
        uploadResult: null,
        imageSpaceId: null,
        name: file.name || '',
      }];
    }
    const remotePreview = await resolveUploadPreviewUrl(storedUrl, {
      ...uploadResult,
      variant: 'thumb',
    });
    return [{
      storedUrl,
      previewUrl: withLocalPreview(file, remotePreview),
      uploadResult,
      imageSpaceId: uploadResult?.id || null,
      name: file.name || '',
    }];
  }

  const batchResult = await imageStorageAPI.saveBatch(list, {
    folder,
    source,
    registerImageSpace,
  });
  const urls = Array.isArray(batchResult?.urls) ? batchResult.urls : [];
  const ids = Array.isArray(batchResult?.ids) ? batchResult.ids : [];
  const viewUrls = batchResult?.viewUrls && typeof batchResult.viewUrls === 'object' && !Array.isArray(batchResult.viewUrls)
    ? batchResult.viewUrls
    : null;
  const displayUrls = batchResult?.displayUrls && typeof batchResult.displayUrls === 'object' && !Array.isArray(batchResult.displayUrls)
    ? batchResult.displayUrls
    : viewUrls;

  // 禁止逐张 await resolveSignedDisplayUrl（会串行打出 N 次 sign-batch）
  const warmRecords = [];
  const needSign = [];
  const prepared = urls.map((url, i) => {
    const storedUrl = String(url || '').trim();
    if (!storedUrl) {
      return {
        storedUrl: '',
        previewUrl: withLocalPreview(list[i], ''),
        uploadResult: null,
        imageSpaceId: null,
        name: list[i]?.name || '',
      };
    }
    const viewUrl = lookupUploadViewUrl(viewUrls, storedUrl)
      || lookupUploadViewUrl(displayUrls, storedUrl)
      || '';
    const uploadResult = {
      url: storedUrl,
      id: ids[i] || null,
      viewUrl: viewUrl || null,
      displayUrl: viewUrl || null,
    };
    if (viewUrl) {
      warmRecords.push({
        id: uploadResult.id,
        url: storedUrl,
        viewUrl,
        displayUrl: viewUrl,
      });
    } else if (isManagedStorageUrl(storedUrl)) {
      needSign.push(toManagedCanonicalUploadsUrl(storedUrl) || storedUrl);
    }
    return {
      storedUrl,
      previewUrl: '',
      uploadResult,
      imageSpaceId: uploadResult.id || null,
      name: list[i]?.name || '',
      _file: list[i],
    };
  });

  if (warmRecords.length > 0) {
    warmDisplayCacheFromRecords(warmRecords);
  }
  if (needSign.length > 0) {
    await ensureDisplayImageUrls([...new Set(needSign)], { variant: 'thumb' });
  }

  return prepared.map((row) => {
    const file = row._file;
    const { _file, ...rest } = row;
    if (!rest.storedUrl) {
      return {
        ...rest,
        previewUrl: rest.previewUrl || withLocalPreview(file, ''),
      };
    }
    const remotePreview = resolveImmediateSignedSrc(rest.storedUrl, {
      viewUrl: rest.uploadResult?.viewUrl,
      displayUrl: rest.uploadResult?.displayUrl,
      imageId: rest.imageSpaceId,
      variant: 'thumb',
    }) || rest.uploadResult?.viewUrl || rest.uploadResult?.displayUrl || '';
    return {
      ...rest,
      previewUrl: withLocalPreview(file, remotePreview),
    };
  });
}

function mapBatchUploadResults(uploadByIndex, fileIndexes, batchResult) {
  const urls = Array.isArray(batchResult?.urls) ? batchResult.urls : [];
  const ids = Array.isArray(batchResult?.ids) ? batchResult.ids : [];
  const viewUrls = batchResult?.viewUrls && typeof batchResult.viewUrls === 'object' && !Array.isArray(batchResult.viewUrls)
    ? batchResult.viewUrls
    : null;
  const displayUrls = batchResult?.displayUrls && typeof batchResult.displayUrls === 'object' && !Array.isArray(batchResult.displayUrls)
    ? batchResult.displayUrls
    : viewUrls;
  fileIndexes.forEach((itemIndex, fileIndex) => {
    const url = urls[fileIndex];
    if (!url) return;
    const viewUrl = lookupUploadViewUrl(viewUrls, url)
      || lookupUploadViewUrl(displayUrls, url)
      || null;
    uploadByIndex.set(itemIndex, {
      url,
      id: ids[fileIndex] || null,
      viewUrl,
      displayUrl: viewUrl,
    });
  });
}

async function finalizeStudioUploadResults(prepared, uploadByIndex) {
  const warmRecords = [];
  const needSign = [];
  const rows = prepared.map((row) => {
    const uploadResult = uploadByIndex.get(row.index) || null;
    let storedUrl = row.needsUpload
      ? (uploadResult?.url || uploadResult?.imageUrl || '')
      : (row.storedUrl || '');
    const trimmed = String(storedUrl || '').trim();
    if (!trimmed || trimmed.startsWith('data:')) {
      return { storedUrl: '', previewUrl: '', uploadResult: null, imageSpaceId: null };
    }
    const viewUrl = uploadResult?.viewUrl || uploadResult?.displayUrl || '';
    if (viewUrl) {
      warmRecords.push({
        id: uploadResult?.id,
        url: trimmed,
        viewUrl,
        displayUrl: viewUrl,
      });
    } else if (isManagedStorageUrl(trimmed)) {
      needSign.push(toManagedCanonicalUploadsUrl(trimmed) || trimmed);
    }
    return {
      storedUrl: trimmed,
      previewUrl: '',
      uploadResult,
      imageSpaceId: uploadResult?.id || null,
    };
  });

  if (warmRecords.length > 0) {
    warmDisplayCacheFromRecords(warmRecords);
  }
  if (needSign.length > 0) {
    await ensureDisplayImageUrls([...new Set(needSign)], { variant: 'thumb' });
  }

  return rows.map((row) => {
    if (!row.storedUrl) return row;
    const previewUrl = resolveImmediateSignedSrc(row.storedUrl, {
      viewUrl: row.uploadResult?.viewUrl,
      displayUrl: row.uploadResult?.displayUrl,
      imageId: row.imageSpaceId,
      variant: 'thumb',
    }) || row.uploadResult?.viewUrl || row.uploadResult?.displayUrl || '';
    return { ...row, previewUrl: previewUrl || '' };
  });
}

/**
 * 将相对路径转换为公网可访问的完整 URL（用于前端图片展示，统一不用 blob）
 * @param {string} url - 相对路径（如 /uploads/xxx.jpg）或完整URL
 * @returns {string} - 完整 URL（如 http://8.148.235.11/uploads/xxx.jpg）
 */
export const getFullImageUrl = (url, options = {}) => {
  if (!url) return '';
  // 优先走统一安全解析；仅当结果已是可加载形态时提前返回（相对 canonical 继续走下方拼 base）
  const fromSigned = resolveDisplayImageUrl(url, options);
  if (fromSigned) {
    if (
      isSignedDisplayUrl(fromSigned)
      || fromSigned.startsWith('http://')
      || fromSigned.startsWith('https://')
      || fromSigned.startsWith('data:')
      || fromSigned.startsWith('blob:')
      || fromSigned.startsWith('indexeddb://')
    ) {
      return isSignedDisplayUrl(fromSigned) && fromSigned.startsWith('/')
        ? toAbsoluteViewPath(fromSigned)
        : fromSigned;
    }
  }
  const normalizedUrl = String(url).trim().replace(/\\/g, '/');
  const urlWithLeadingSlash = normalizedUrl.startsWith('uploads/')
    ? `/${normalizedUrl}`
    : normalizedUrl;
  
  // 完整 URL：受管走缓存/签名；本地绝对 uploads 可直出；裸 OSS 清空
  if (urlWithLeadingSlash.startsWith('http://') || urlWithLeadingSlash.startsWith('https://')) {
    const resolved = resolveDisplayImageUrl(urlWithLeadingSlash, options);
    if (resolved) return resolved;
    try {
      const path = new URL(urlWithLeadingSlash).pathname || '';
      if (path.startsWith('/uploads/') || path.startsWith('/api/uploads/')) {
        return urlWithLeadingSlash;
      }
    } catch {
      // ignore
    }
    if (isSignedViewEnabled() && isManagedStorageUrl(urlWithLeadingSlash)) return '';
    return urlWithLeadingSlash;
  }

  if (urlWithLeadingSlash.startsWith('/api/storage/images/view')) {
    return toAbsoluteViewPath(urlWithLeadingSlash);
  }

  // local/oss 统一：受管 /uploads/ 走 sign-batch；无缓存时不回退裸 canonical（由 resolveSignedDisplayUrl 异步兜底）
  if (isSignedViewEnabled() && isManagedStorageUrl(urlWithLeadingSlash)) {
    return resolveDisplayImageUrl(urlWithLeadingSlash, options) || '';
  }

  // REACT_APP_IMAGE_SIGNED_VIEW=false 时回退公网直链（local dev 紧急调试）
  if (!isSignedViewEnabled() && urlWithLeadingSlash.startsWith('/uploads/')) {
    const base = resolveAssetBaseUrl();
    return `${base}${urlWithLeadingSlash.startsWith('/') ? '' : '/'}${urlWithLeadingSlash}`;
  }

  // signed-only：白名单外 /uploads/ 也不拼裸链（后端 deny → 403）
  if (isSignedViewEnabled() && isUploadsStoragePath(urlWithLeadingSlash)) {
    return '';
  }

  // 前端 public 静态资源：走当前站点 origin，避免拼到 API 域名导致 404
  if (
    typeof window !== 'undefined' &&
    (urlWithLeadingSlash.startsWith('/images/')
      || urlWithLeadingSlash.startsWith('/video-concept-previews/')
      || urlWithLeadingSlash.startsWith('/assets/'))
  ) {
    return `${window.location.origin}${urlWithLeadingSlash}`;
  }

  // 优先使用 server URL；blob/data 仅作兼容（旧数据未上传时仍可显示，新保存会带 serverUrl）
  if (urlWithLeadingSlash.startsWith('indexeddb://') || urlWithLeadingSlash.startsWith('blob:') || urlWithLeadingSlash.startsWith('data:')) {
    return urlWithLeadingSlash;
  }
  
  return urlWithLeadingSlash.startsWith('/') ? `${resolveAssetBaseUrl()}${urlWithLeadingSlash}` : urlWithLeadingSlash;
};

/**
 * 将误指向前端 dev 端口的 /uploads/ 完整 URL 改写到 API/公网 base，避免 canvas 加载 404/500。
 */
export const rebaseUploadsUrl = (url) => {
  if (!url) return '';
  const s = String(url).trim();
  if (!s || s.startsWith('data:') || s.startsWith('blob:') || s.startsWith('indexeddb://')) {
    return s;
  }
  let path = s;
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const parsed = new URL(s);
      path = `${parsed.pathname || ''}${parsed.search || ''}`;
    } catch {
      return s;
    }
  }
  const uploadsIdx = path.indexOf('/uploads/');
  if (uploadsIdx >= 0) {
    const canonical = path.slice(uploadsIdx);
    const resolved = getFullImageUrl(canonical);
    if (resolved) return resolved;
    if (!isSignedViewEnabled()) return canonical;
    return isUploadsStoragePath(canonical) ? '' : canonical;
  }
  const resolved = getFullImageUrl(s);
  if (resolved) return resolved;
  if (!isSignedViewEnabled()) return s;
  return isUploadsStoragePath(s) ? '' : s;
};

/**
 * 将图片URL转换为服务器存储的URL（如是 indexeddb/blob/data，先取数据再上传到服务器）
 * @param {string} imageUrl - 图片URL（可能是 indexeddb://、blob:、data: 或 /uploads/）
 * @returns {Promise<string>} - 服务器URL（如 /uploads/xxx.jpg）
 */
export const convertImageToServerUrl = async (imageUrl, options = {}) => {
  if (!imageUrl || typeof imageUrl !== 'string') return '';
  const { folder = getImageSpaceUploadFolder() } = options;
  const saveOptions = {
    folder,
    registerImageSpace: options.registerImageSpace === true,
  };

  // indexeddb://id：仅本设备有数据，先读出再转为服务器 URL，便于跨设备
  if (imageUrl.startsWith('indexeddb://')) {
    const id = imageUrl.replace(/^indexeddb:\/\//, '').trim();
    if (!id) return '';
    const localUrl = await getImageFromIndexedDB(id);
    if (!localUrl) return '';
    if (localUrl.startsWith('/uploads/')) return localUrl;
    const normalized = normalizePersistedImageUrl(localUrl);
    if (normalized) return normalized;
    return await convertImageToServerUrl(localUrl, options);
  }

  const persisted = normalizePersistedImageUrl(imageUrl);
  if (persisted) return persisted;

  // 如果是data URL，直接保存
  if (imageUrl.startsWith('data:')) {
    try {

      const result = await imageStorageAPI.save(imageUrl, saveOptions);
      const savedUrl = pickUrlFromImageStorageResponse(result);
      if (savedUrl) return savedUrl;
    } catch (error) {
      console.error('[图片URL工具] 保存data URL失败:', error);
      return '';
    }
  }

  // 如果是 blob URL，直接 multipart 上传
  if (imageUrl.startsWith('blob:')) {
    try {
      const result = await imageStorageAPI.save(imageUrl, saveOptions);
      const savedUrl = pickUrlFromImageStorageResponse(result);
      if (savedUrl) return savedUrl;
    } catch (error) {
      console.error('[图片URL工具] 转换 blob URL 失败:', error);
      return '';
    }
  }

  return '';
};

async function getPersistedUrlFromIndexedDB(imageId) {
  if (!imageId) return '';
  try {
    const localUrl = await getImageFromIndexedDB(imageId);
    if (!localUrl || String(localUrl).startsWith('blob:') || String(localUrl).startsWith('data:')) {
      return '';
    }
    return normalizePersistedImageUrl(localUrl) || '';
  } catch {
    return '';
  }
}

function isInlineImagePayload(url) {
  const s = String(url || '').trim();
  return !s || s.startsWith('data:') || s.startsWith('blob:') || s.startsWith('indexeddb://');
}

/** 落盘后把同条记录里残留的 imageUrl/src 也改成 /uploads，避免 JSON 仍带 base64 */
function applyPersistedSpaceFields(item, persistedUrl, extra = {}) {
  const url = String(persistedUrl || '').trim();
  const next = {
    ...item,
    url,
    dataUrl: null,
    compositeUrl: undefined,
    uploadFile: undefined,
    ...extra,
  };
  ['imageUrl', 'src', 'originalUrl'].forEach((key) => {
    if (!(key in next)) return;
    if (!isInlineImagePayload(next[key])) return;
    if (url) next[key] = url;
    else delete next[key];
  });
  return next;
}

/** 图片空间入库前解析可上传的图片来源（合成图等常把 dataUrl 放在独立字段） */
function pickImageSpaceUploadSource(item) {
  const fields = [
    item?.url,
    item?.dataUrl,
    item?.compositeUrl,
    item?.originalDataUrl,
    item?.compressedDataUrl,
  ];
  for (const value of fields) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

/**
 * 将图片空间项中的 url 转为服务器 URL（indexeddb/data/blob 会先上传再返回路径），便于跨设备同步
 * @param {Array<{ id: string, url?: string, [k: string]: any }>} items - 图片空间项列表
 * @param {{ folder?: string }} [options] - 上传目录，默认 image-space
 * @returns {Promise<Array<{ id: string, url: string, [k: string]: any }>>} - 每项 url 为 /uploads/、OSS https URL 或原样
 */
export const ensureImageSpaceItemsServerUrl = async (items, options = {}) => {
  const folder = options.folder || getImageSpaceUploadFolder();
  const skipCompress = options.skipCompress === true
    || (Array.isArray(items) && items.length > 0 && items.every((item) => (
      item?._preCompressed === true || item?.uploadFile instanceof File
    )));
  if (!Array.isArray(items) || items.length === 0) return items;

  const result = new Array(items.length);
  /** @type {Array<{ index: number, item: object, source: string, uploadFile?: File }>} */
  const pending = [];

  await Promise.all(items.map(async (item, index) => {
    if (item?.uploadFile instanceof File) {
      pending.push({ index, item, source: '', uploadFile: item.uploadFile });
      return;
    }
    const url = pickImageSpaceUploadSource(item);
    if (!url || url.startsWith('indexeddb://') || url.startsWith('data:') || url.startsWith('blob:')) {
      const cached = await getPersistedUrlFromIndexedDB(item?.id);
      if (cached) {
        result[index] = applyPersistedSpaceFields(item, cached);
        return;
      }
      pending.push({ index, item, source: url || `indexeddb://${item?.id || ''}` });
      return;
    }
    const normalized = normalizePersistedImageUrl(url);
    result[index] = normalized
      ? applyPersistedSpaceFields(item, normalized)
      : item;
  }));

  if (pending.length === 0) return result;

  // 单张仍走原路径；多张改 saveBatch，避免 N 次 capabilities / upload / 去重拉全量
  if (pending.length === 1 && !(pending[0].uploadFile instanceof File)) {
    const { index, item, source } = pending[0];
    const serverUrl = await convertImageToServerUrl(source, {
      folder,
      registerImageSpace: false,
    });
    result[index] = applyPersistedSpaceFields(item, serverUrl || '');
    return result;
  }

  const { buildUploadFileFromBlob } = await import('./ipeImageUploadFormats');
  const files = [];
  const fileMeta = [];
  for (const row of pending) {
    try {
      if (row.uploadFile instanceof File) {
        files.push(row.uploadFile);
        fileMeta.push(row);
        continue;
      }
      let blob = null;
      let dataUrlHint = '';
      let preferredName = row.item?.name || `image-${row.index + 1}.jpg`;
      const source = String(row.source || '').trim();
      if (source.startsWith('indexeddb://')) {
        const id = source.replace(/^indexeddb:\/\//, '').trim();
        const localUrl = id ? await getImageFromIndexedDB(id) : '';
        if (!localUrl) {
          result[row.index] = applyPersistedSpaceFields(row.item, '');
          continue;
        }
        const normalized = normalizePersistedImageUrl(localUrl);
        if (normalized) {
          result[row.index] = applyPersistedSpaceFields(row.item, normalized);
          continue;
        }
        if (localUrl.startsWith('data:')) {
          blob = dataUrlToBlob(localUrl);
          dataUrlHint = localUrl;
        } else if (localUrl.startsWith('blob:')) {
          const response = await fetch(localUrl);
          blob = await response.blob();
        } else {
          result[row.index] = applyPersistedSpaceFields(row.item, '');
          continue;
        }
      } else if (source.startsWith('data:')) {
        blob = dataUrlToBlob(source);
        dataUrlHint = source;
      } else if (source.startsWith('blob:')) {
        const response = await fetch(source);
        blob = await response.blob();
      }
      if (!blob) {
        result[row.index] = applyPersistedSpaceFields(row.item, '');
        continue;
      }
      const file = await buildUploadFileFromBlob(blob, { filename: preferredName, dataUrlHint });
      files.push(file);
      fileMeta.push(row);
    } catch (error) {
      console.warn('[图片URL工具] 批量转存准备失败', error);
      result[row.index] = applyPersistedSpaceFields(row.item, '');
    }
  }

  if (files.length === 0) return result;

  const applyBatchUrls = (batchResult, metaRows) => {
    const urls = Array.isArray(batchResult?.urls) ? batchResult.urls : [];
    const viewUrlMap = batchResult?.viewUrls && typeof batchResult.viewUrls === 'object' && !Array.isArray(batchResult.viewUrls)
      ? batchResult.viewUrls
      : null;
    const displayUrlMap = batchResult?.displayUrls && typeof batchResult.displayUrls === 'object' && !Array.isArray(batchResult.displayUrls)
      ? batchResult.displayUrls
      : viewUrlMap;
    metaRows.forEach((row, fileIndex) => {
      const persisted = normalizePersistedImageUrl(urls[fileIndex]) || '';
      const viewUrl = lookupUploadViewUrl(viewUrlMap, urls[fileIndex])
        || lookupUploadViewUrl(viewUrlMap, persisted)
        || lookupUploadViewUrl(displayUrlMap, urls[fileIndex])
        || lookupUploadViewUrl(displayUrlMap, persisted)
        || '';
      result[row.index] = applyPersistedSpaceFields(row.item, persisted, {
        ...(viewUrl ? { viewUrl, displayUrl: viewUrl } : {}),
        _preCompressed: undefined,
      });
    });
  };

  const saveBatchOnce = (fileList) => imageStorageAPI.saveBatch(fileList, {
    folder,
    registerImageSpace: false,
    skipCompress,
  });

  try {
    let batchResult;
    try {
      batchResult = await saveBatchOnce(files);
    } catch (firstError) {
      console.warn('[图片URL工具] 批量转存失败，整批重试 1 次', firstError?.message || firstError);
      batchResult = await saveBatchOnce(files);
    }
    applyBatchUrls(batchResult, fileMeta);
  } catch (error) {
    // 禁止 N× saveBatch([one]) 风暴；改为更小分片串行重试
    console.warn('[图片URL工具] 批量转存仍失败，按小分片重试', error?.message || error);
    const SMALL_CHUNK = 5;
    for (let offset = 0; offset < files.length; offset += SMALL_CHUNK) {
      const sliceFiles = files.slice(offset, offset + SMALL_CHUNK);
      const sliceMeta = fileMeta.slice(offset, offset + SMALL_CHUNK);
      try {
        const part = await saveBatchOnce(sliceFiles);
        applyBatchUrls(part, sliceMeta);
      } catch (chunkError) {
        console.warn(
          `[图片URL工具] 小分片转存失败 offset=${offset} size=${sliceFiles.length}`,
          chunkError?.message || chunkError,
        );
        sliceMeta.forEach((row) => {
          result[row.index] = applyPersistedSpaceFields(row.item, '', {
            _preCompressed: undefined,
          });
        });
      }
    }
  }

  return result;
};

/**
 * 解析可复制到剪贴板的图片地址：受管存储走签名 viewUrl，避免 signed-only 下复制裸 /uploads/ 直链。
 */
export async function resolveCopyableImageUrl(imageUrl, options = {}) {
  let url = String(imageUrl || '').trim();
  if (!url) return '';

  if (url.startsWith('data:') || url.startsWith('blob:')) return '';

  const canonical = normalizePersistedImageUrl(url) || url;

  if (isSignedViewEnabled() && isManagedStorageUrl(canonical)) {
    const signed = await resolveSignedDisplayUrl(canonical, options);
    return sanitizeManagedDisplayResult(signed, canonical) || signed || '';
  }

  if (isSignedViewEnabled() && isUploadsStoragePath(canonical)) {
    return '';
  }

  if (canonical.startsWith('/uploads/')) {
    return `${resolveAssetBaseUrl()}${canonical}`;
  }

  if (!canonical.startsWith('http://') && !canonical.startsWith('https://')) {
    return canonical.startsWith('/') ? `${resolveAssetBaseUrl()}${canonical}` : canonical;
  }

  return canonical;
}

/**
 * 复制图片URL到剪贴板（如果是dataURL或blob URL，先转换为服务器URL）
 * @param {string} imageUrl - 图片URL
 * @param {object} [options] - displayUrl/viewUrl 等签名优先字段
 * @returns {Promise<boolean>} - 是否复制成功
 */
export const copyImageUrl = async (imageUrl, options = {}) => {
  if (!imageUrl) {
    return false;
  }

  // 如果是data URL，必须先转换为服务器URL，不能直接复制data URL
  if (imageUrl.startsWith('data:')) {

    const serverUrl = await convertImageToServerUrl(imageUrl);
    if (!serverUrl) {
      console.error('[图片URL工具] data URL转换为服务器URL失败，无法复制');
      return false;
    }
    // 使用转换后的服务器URL继续处理
    imageUrl = serverUrl;
  }

  // 如果是blob URL，也必须先转换为服务器URL
  if (imageUrl.startsWith('blob:')) {

    const serverUrl = await convertImageToServerUrl(imageUrl);
    if (!serverUrl) {
      console.error('[图片URL工具] blob URL转换为服务器URL失败，无法复制');
      return false;
    }
    // 使用转换后的服务器URL继续处理
    imageUrl = serverUrl;
  }

  try {
    const finalUrl = await resolveCopyableImageUrl(imageUrl, options);

    // 再次检查：确保最终URL不是data URL或blob URL
    if (!finalUrl || finalUrl.startsWith('data:') || finalUrl.startsWith('blob:')) {
      console.error('[图片URL工具] 最终URL仍然是data URL或blob URL，拒绝复制');
      return false;
    }

    // 复制到剪贴板
    await navigator.clipboard.writeText(finalUrl);
    return true;
  } catch (error) {
    console.error('[图片URL工具] 复制失败:', error);
    // 降级方案：使用传统的复制方法
    try {
      const finalUrl = await resolveCopyableImageUrl(imageUrl, options);

      // 再次检查：确保最终URL不是data URL或blob URL
      if (!finalUrl || finalUrl.startsWith('data:') || finalUrl.startsWith('blob:')) {
        console.error('[图片URL工具] 降级方案：最终URL仍然是data URL或blob URL，拒绝复制');
        return false;
      }

      const textArea = document.createElement('textarea');
      textArea.value = finalUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (fallbackError) {
      console.error('[图片URL工具] 降级复制方案也失败:', fallbackError);
      return false;
    }
  }
};
